import React, { useState, useCallback, useEffect } from "react";
import EditInBondHeader from './editinbondheader'
import EditInbondentryform from './editinbondentryform'
import { useNavigate } from "react-router-dom";
import CancelButton from '../common/ui/cancelbutton';
import { useLoader } from "../../context/loadercontext";
import Button from '../common/ui/button';
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import WarningModal from "../common/ui/warningmodel";

export default function EditInBond() {
    const { setLoading } = useLoader();
    const [binumber, setBINumber] = useState([]);
    const [loadingBINumber, setLoadingBINumber] = useState(false);
    const [materials, setMaterials] = useState([]);
    const [loadingMaterials, setLoadingMaterials] = useState(false);
    const [childrecords, setchildRecords] = useState([]);
    const [masterrecords, setmasterRecords] = useState([]);
    const navigate = useNavigate();
    const [warningVisible, setWarningVisible] = useState(false);
    const [warningMessage, setWarningMessage] = useState("");
    const [formData, setFormData] = useState({
        biNumber: "",
        beNumber: "",
        beDate: "",
        inbondDate: "",
        material: null,
        weight: "",
        invoiceValue: "",
        dollar: "",
        price: "",
        duty: "",
        assessment: "",
        totalAmount: "",
    });
    const location = useLocation();
    const { parentId, childId } = location.state || {};
    const { logout } = useAuth();

    // === Utilities ===
    const updateFormData = useCallback((field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    }, []);

    // === Fetch BI Numbers ===
    const fetchBINumber = useCallback(async () => {
        setLoading(true);
        const startTime = Date.now();
        try {
            setLoadingBINumber(true);

            let response = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.GET_ALL_BI_NUMBER}`,
                { method: "GET", headers: getHeaders(true) }
            );
            if (response.status === 401) {
                console.warn("Access token expired. Attempting refresh...");

                const newToken = await refreshAccessToken();

                if (newToken) {
                    // Retry API call with new token
                    response = await fetch(
                        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.GET_ALL_BI_NUMBER}`,
                        { method: "GET", headers: getHeaders(true) }
                    );
                } else {
                    console.error("Refresh token failed. Logging out...");
                    logout(); // redirect user
                    return;
                }
            }

            const result = await response.json();
            if (result.status === 200 && Array.isArray(result.data)) {
                setBINumber(
                    result.data.map((item) => ({ label: item.binumber, value: item.id }))
                );
            } else {
                setBINumber([]);
            }
        } catch (error) {
            console.error("Error fetching BI number:", error);
            setBINumber([]);
        } finally {
            const elapsed = Date.now() - startTime;
            const remainingDelay = Math.max(0, 1000 - elapsed);
            setTimeout(() => setLoading(false), remainingDelay);
            setLoadingBINumber(false);
        }
    }, [setLoading, logout]);

    // === Fetch Materials ===
    const fetchMaterials = useCallback(async () => {
        setLoading(true);
        const start = Date.now();
        try {
            setLoadingMaterials(true);
            let res = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.GET_NAME}`,
                { method: "GET", headers: getHeaders(true) }
            );

            // -------------------------
            // 🔥 If ACCESS TOKEN expired
            // -------------------------
            if (res.status === 401) {
                console.warn("Access token expired. Attempting refresh...");

                const newToken = await refreshAccessToken();

                if (newToken) {
                    // Retry API call with new token
                    res = await fetch(
                        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.GET_NAME}`,
                        { method: "GET", headers: getHeaders(true) }
                    );
                } else {
                    console.error("Refresh token failed. Logging out...");
                    logout(); // redirect user
                    return;
                }
            }

            const result = await res.json();
            if (result.status === 200 && Array.isArray(result.data)) {
                setMaterials(
                    result.data.map((m) => ({
                        label: `${m.material_short_code.toUpperCase()}`,
                        value: m.material_id,
                        nameOnly: m.material_name
                    }))
                );
            } else {
                setMaterials([]);
            }
        } catch (err) {
            console.error("Error fetching materials:", err);
            setMaterials([]);
        } finally {
            const elapsed = Date.now() - start;
            setTimeout(() => setLoading(false), Math.max(0, 1000 - elapsed));
            setLoadingMaterials(false);
        }
    }, [setLoading, logout]);

    // === Fetch Edit Details ===

    const fetchEditDetails = useCallback(async () => {
        if (!parentId) return;
        const safeChildId = childId ?? "";
        setLoading(true);

        try {
            const callApi = async () =>
                await fetch(
                    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.GET_EDIT_DETAILS(parentId, safeChildId)}`,
                    { headers: getHeaders(true) }
                );

            let res = await callApi();

            if (res.status === 401) {
                const newToken = await refreshAccessToken();
                if (newToken) res = await callApi();
                else return logout();
            }

            const result = await res.json();

            if (result.status === 200 && result.data) {
                const { master_data, child_data } = result.data;

                const updatedFormData = {
                    biNumber: master_data?.bi_number || "",
                    beNumber: master_data?.be_number || "",
                    beDate: master_data?.be_date || "",
                    inbondDate: master_data?.inbond_date || "",
                    totalDuty: master_data?.total_duty_inbond_amount_inr || 0,
                    totalWeight: master_data?.total_weight || 0,
                    totalAssessment: master_data?.total_assessment_amount_inr || 0,
                    totalMaterialUSD: master_data?.total_material_amount_usd || 0,
                    material: child_data?.material_master_id
                        ? String(child_data.material_master_id)
                        : "",
                    weight: child_data?.weight,
                    invoiceValue: "",
                    dollar: "",
                    price: "",
                    duty: "",
                    assessment: "",
                    totalAmount: "",
                };

                setFormData(updatedFormData);
                setchildRecords(Array.isArray(child_data) ? child_data : (child_data ? [child_data] : []));
                setmasterRecords(master_data ? { master: master_data } : {});
            } else {
                setFormData({});
                setchildRecords([]);
                setmasterRecords({});
            }
        } catch (err) {
            console.error("Error fetching edit details:", err);
            setFormData({});
            setchildRecords([]);
            setmasterRecords({});
        } finally {
            setLoading(false);
        }
    }, [parentId, childId, setLoading, logout]);



    // Load select lists ONCE
    useEffect(() => {
        fetchBINumber();
        fetchMaterials();
    }, [fetchBINumber, fetchMaterials]);


    // Load edit data ONCE
    useEffect(() => {
        fetchEditDetails();
    }, [fetchEditDetails]);



    const canSubmit =
        formData.biNumber &&
        formData.beNumber &&
        formData.beDate &&
        formData.inbondDate;

    const handleSubmit = async () => {
        const payload = {
            bi_number: formData.biNumber,
            be_number: formData.beNumber,
            be_date: formData.beDate,
            inbond_date: formData.inbondDate,
            total_duty_inbond_amount_inr: childrecords.reduce(
                (acc, r) => acc + Number(r.duty_inbond_amount_inr || 0),
                0
            ),
            total_weight: childrecords.reduce((acc, r) => acc + Number(r.weight || 0), 0),
            total_assessment_amount_inr: childrecords.reduce(
                (acc, r) => acc + Number(r.assessment_amount_inr || 0),
                0
            ),
            total_material_amount_usd: childrecords.reduce(
                (acc, r) => acc + Number(r.material_amount_usd || 0),
                0
            ),
            inbondchild: childrecords.map((r) => ({
                id: r.id,
                material_master_id: r.material_master_id,
                duty_inbond_amount_inr: Number(r.duty_inbond_amount_inr || 0),
                weight: Number(r.weight || 0),
                invoice_amount_usd: Number(r.invoice_amount_usd || 0),
                assessment_amount_inr: Number(r.assessment_amount_inr || 0),
                dollar_inr: Number(r.dollar_inr || 0),
                price: Number(r.price || 0),
                material_amount_usd: Number(r.material_amount_usd || 0),
            })),
        };

        try {
            setLoading(true);

            // Helper function for API call
            const callApi = async () =>
                await fetch(
                    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.UPDATE_COMPLETE(parentId)}`,
                    {
                        method: "PUT",
                        headers: { ...getHeaders(true), "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                    }
                );

            // Initial API call
            let response = await callApi();

            // -------------------------
            // 🔥 Handle 401 token expired
            // -------------------------
            if (response.status === 401) {
                console.warn("Access token expired. Attempting refresh...");
                const newToken = await refreshAccessToken();

                if (newToken) {
                    // Retry API call with refreshed token
                    response = await callApi();
                } else {
                    console.error("Refresh token failed. Logging out...");
                    logout();
                    return;
                }
            }

            const result = await response.json();

            if (result.status === 201 || result.status === 200) {
                setchildRecords([]); // clear all entries
                setmasterRecords({});
                navigate("/in-bond", {
                    state: {
                        showToast: true,
                        message: result.message || "In-bond updated successfully!",
                    },
                });
            } else {
                setWarningMessage(result.message || "Failed to update in-bond.");
                setWarningVisible(true);
            }
        } catch (err) {
            console.error("Error submitting inbond:", err);
            setWarningMessage("Something went wrong.");
            setWarningVisible(true);
        } finally {
            setLoading(false);
        }
    };



    return (
        <>
            <div className="w-full px-2 md:px-4">
                <EditInBondHeader />
                <EditInbondentryform
                    formData={formData}
                    updateFormData={updateFormData}
                    biNumberList={binumber}
                    loadingBINumber={loadingBINumber}
                    materialList={materials}
                    loadingMaterials={loadingMaterials}
                    childrecords={childrecords}
                    setchildRecords={setchildRecords}
                    masterrecords={masterrecords}
                    setmasterRecords={setmasterRecords}
                    selectedchildId={childId}
                    selectedparentId={parentId}
                />
                <div className="flex justify-end gap-4 mt-4">
                    <CancelButton onClick={() => navigate("/in-bond")}>Cancel</CancelButton>
                    <Button
                        bgColor="bg-colorButton"
                        textColor="text-white"
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </div>
            </div>
            {warningVisible && (
                <WarningModal
                    message={warningMessage}
                    onClose={() => setWarningVisible(false)}
                />
            )}

        </>
    )
}